import { useQuery } from "@tanstack/react-query";
import type { MasterDataEntityType } from "@/lib/enums";
import { copyOperationExtractedValueKeys } from "@/lib/query";

async function extractErrorMessage(res: Response, fallback: string): Promise<string> {
  const json = await res.json().catch(() => null);
  return json?.error ?? fallback;
}

/**
 * Non-admin-readable harvested values for one BU + entity type — feeds the
 * staging editor's reference dropdowns for the TARGET BU (the admin-only
 * `/api/admin/master-data-extracted-values` route backs the Master Data
 * Mappings admin form instead; this is the same underlying cache, exposed
 * for the copy flow). See `.claude/rules` — non-admin exception, same
 * posture as `useActiveBuInstances`.
 */
export function useExtractedValues(
  buInstanceId: string | null,
  entityType: MasterDataEntityType,
  search = "",
  enabled = true,
) {
  return useQuery({
    queryKey: copyOperationExtractedValueKeys.list({
      buInstanceId: buInstanceId ?? "",
      entityType,
      search,
    }),
    queryFn: async (): Promise<string[]> => {
      const params = new URLSearchParams({ buInstanceId: buInstanceId ?? "", entityType });
      if (search.trim()) params.set("search", search.trim());
      const res = await fetch(`/api/copy-operations/extracted-values?${params}`);
      if (!res.ok)
        throw new Error(await extractErrorMessage(res, "Failed to fetch reference values"));
      const json = await res.json();
      return json.data.map((row: { value: string }) => row.value);
    },
    enabled: Boolean(buInstanceId) && enabled,
  });
}
